'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { MonthPicker, YearPicker } from '@workspace/ui/components/calendar';
import { Input } from '@workspace/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { cn } from '@workspace/ui/lib';
import { format } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import * as React from 'react';

export const PICKER_MODE = {
  // Year only picker
  YEAR_ONLY: 'year-only',
  // Month only picker
  MONTH_ONLY: 'month-only',
  // Year and month picker
  YEAR_MONTH: 'year-month',
  // Year picker is optional (only if the user clicks on the year selector), month picker is mandatory and always shown by default
  MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR: 'mandatory-month-select-and-optional-year',
} as const;

type PickerMode = (typeof PICKER_MODE)[keyof typeof PICKER_MODE];

function normalizeMonthStart(date: Date): Date {
  const normalized = new Date(date.getFullYear(), date.getMonth(), 1);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function getMonthIndex(year: number, monthIndex: number): number {
  return year * 12 + monthIndex;
}

interface YearMonthPickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  onMonthSelect?: (date: Date) => void;
  mode?: PickerMode;
  formatValue?: (date: Date) => string;
  inputClassName?: string;
  clearable?: boolean;
  shouldDisablePastDates?: boolean;
  minMonth?: Date;
  maxMonth?: Date;
  size?: 'base' | 'sm';
}

export function YearMonthPicker({
  value,
  onChange,
  placeholder,
  disabled,
  onMonthSelect,
  mode = PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR,
  formatValue,
  inputClassName,
  clearable = true,
  shouldDisablePastDates = false,
  minMonth,
  maxMonth,
  size = 'base',
}: YearMonthPickerProps) {
  const [showYearPicker, setShowYearPicker] = React.useState(false);
  const [showMonthPicker, setShowMonthPicker] = React.useState(false);
  const [tempYear, setTempYear] = React.useState<Date | undefined>(undefined);

  const handleClear = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    onChange(undefined);
    setTempYear(undefined);
    setShowYearPicker(false);
    setShowMonthPicker(false);
  };

  const handleYearSelect = (date: Date) => {
    if (mode === PICKER_MODE.YEAR_ONLY) {
      onChange(date);
      setShowYearPicker(false);
    } else if (
      mode === PICKER_MODE.YEAR_MONTH ||
      mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR
    ) {
      setTempYear(date);
      setShowYearPicker(false);
      setShowMonthPicker(true);
    }
  };

  const handleMonthSelect = (date: Date) => {
    if (mode === PICKER_MODE.YEAR_MONTH && tempYear) {
      // Combine the selected year with the selected month
      // Set day to 1 first to prevent month overflow issues
      const finalDate = new Date(tempYear);
      finalDate.setDate(1);
      finalDate.setMonth(date.getMonth());
      onChange(finalDate);
      setTempYear(undefined);
    } else if (mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR) {
      const yearToUse = tempYear ?? value ?? new Date();
      const finalDate = new Date(yearToUse);
      finalDate.setDate(1);
      finalDate.setMonth(date.getMonth());
      onChange(finalDate);
      setTempYear(undefined);
    } else {
      onChange(date);
    }
    setShowMonthPicker(false);
    onMonthSelect?.(date);
  };

  const getDisplayValue = () => {
    if (!value) return placeholder || 'Select month';

    if (formatValue) {
      return formatValue(value);
    }

    switch (mode) {
      case PICKER_MODE.YEAR_ONLY:
        return format(value, 'yyyy');
      case PICKER_MODE.MONTH_ONLY:
        return format(value, 'LLLL');
      default:
        return format(value, 'LLLL yyyy');
    }
  };

  const handleInputClick = () => {
    if (disabled) return;

    switch (mode) {
      case PICKER_MODE.YEAR_ONLY:
        setShowYearPicker(true);
        break;
      case PICKER_MODE.MONTH_ONLY:
        setShowMonthPicker(true);
        break;
      case PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR:
        setTempYear(value ?? new Date());
        setShowMonthPicker(true);
        break;
      default:
        setShowYearPicker(true);
        break;
    }
  };

  const isPickerOpen = showYearPicker || showMonthPicker;

  // Use tempYear for year picker display when in year-month or month-mandatory-select-and-year mode
  const yearPickerDate =
    (mode === PICKER_MODE.YEAR_MONTH ||
      mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR) &&
    tempYear
      ? tempYear
      : (value ?? new Date());

  const normalizedMinMonth = React.useMemo(
    () => (minMonth ? normalizeMonthStart(minMonth) : undefined),
    [minMonth]
  );
  const normalizedMaxMonth = React.useMemo(
    () => (maxMonth ? normalizeMonthStart(maxMonth) : undefined),
    [maxMonth]
  );

  const minYear = normalizedMinMonth?.getFullYear();
  const maxYear = normalizedMaxMonth?.getFullYear();

  const isMonthDisabled = React.useCallback(
    (monthIndex: number) => {
      const now = new Date();
      const year = (tempYear ?? value ?? new Date()).getFullYear();
      const monthStartIndex = getMonthIndex(year, monthIndex);

      if (
        shouldDisablePastDates &&
        (year < now.getFullYear() || (year === now.getFullYear() && monthIndex < now.getMonth()))
      ) {
        return true;
      }

      if (
        normalizedMinMonth &&
        monthStartIndex <
          getMonthIndex(normalizedMinMonth.getFullYear(), normalizedMinMonth.getMonth())
      ) {
        return true;
      }

      if (
        normalizedMaxMonth &&
        monthStartIndex >
          getMonthIndex(normalizedMaxMonth.getFullYear(), normalizedMaxMonth.getMonth())
      ) {
        return true;
      }

      return false;
    },
    [
      shouldDisablePastDates,
      tempYear,
      value,
      normalizedMinMonth,
      normalizedMaxMonth,
      minYear,
      maxYear,
    ]
  );

  const isYearDisabled = React.useCallback(
    (year: number) => {
      if (shouldDisablePastDates && year < new Date().getFullYear()) {
        return true;
      }

      if (minYear != null && year < minYear) {
        return true;
      }

      if (maxYear != null && year > maxYear) {
        return true;
      }

      return false;
    },
    [shouldDisablePastDates, minYear, maxYear]
  );

  const clampYearNavigation = React.useCallback(
    (date: Date, direction: -1 | 1) => {
      const nextDate = new Date(date);
      nextDate.setFullYear(nextDate.getFullYear() + direction);

      if (minYear != null && nextDate.getFullYear() < minYear) {
        nextDate.setFullYear(minYear);
      }

      if (maxYear != null && nextDate.getFullYear() > maxYear) {
        nextDate.setFullYear(maxYear);
      }

      return nextDate;
    },
    [minYear, maxYear]
  );

  return (
    <Popover
      open={isPickerOpen}
      onOpenChange={(open) => {
        if (!open) {
          setShowYearPicker(false);
          setShowMonthPicker(false);
          setTempYear(undefined);
        }
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <div
          data-year-month-picker
          className={cn(
            'relative w-full outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-0',
            size === 'sm' ? 'rounded-xl' : 'rounded-2xl',
            !disabled && 'cursor-pointer',
            disabled && 'pointer-events-none'
          )}
          onClick={handleInputClick}
          aria-label="Select month"
        >
          <Input
            className={cn(
              'w-full text-left!',
              value &&
                !disabled &&
                'text-neutral-200! dark:text-neutral-100! hover:text-neutral-200! dark:hover:text-neutral-100!',
              !value &&
                !disabled &&
                'text-grey-700! dark:text-grey-500! hover:text-grey-700! dark:hover:text-grey-500!',
              disabled && 'text-grey-300! dark:text-grey-900!',
              inputClassName,
              size === 'sm' && 'h-9 rounded-xl px-3 py-2.5 text-sm leading-none!',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            )}
            value={getDisplayValue()}
            endIcon={
              value && clearable ? (
                <X
                  className={cn(
                    'text-primary dark:text-neutral-100',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  )}
                  onClick={handleClear}
                  onMouseDown={(e) => e.preventDefault()}
                />
              ) : (
                <CalendarIcon
                  className={cn(
                    'text-primary dark:text-neutral-100',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  )}
                />
              )
            }
            disabled={disabled}
            readOnly
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-fit min-w-0 p-0">
        {showYearPicker && (
          <YearPicker
            className="w-[356px]!"
            selectedDate={yearPickerDate}
            onSelect={handleYearSelect}
            onPrev={() => {
              const newDate = new Date(yearPickerDate);
              newDate.setFullYear(newDate.getFullYear() - 15);
              const clampedDate =
                minYear != null && newDate.getFullYear() < minYear
                  ? new Date(minYear, newDate.getMonth(), 1)
                  : newDate;
              if (
                mode === PICKER_MODE.YEAR_MONTH ||
                mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR
              ) {
                setTempYear(clampedDate);
              } else {
                onChange(clampedDate);
              }
            }}
            onNext={() => {
              const newDate = new Date(yearPickerDate);
              newDate.setFullYear(newDate.getFullYear() + 15);
              const clampedDate =
                maxYear != null && newDate.getFullYear() > maxYear
                  ? new Date(maxYear, newDate.getMonth(), 1)
                  : newDate;
              if (
                mode === PICKER_MODE.YEAR_MONTH ||
                mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR
              ) {
                setTempYear(clampedDate);
              } else {
                onChange(clampedDate);
              }
            }}
            disabledYear={isYearDisabled}
          />
        )}
        {showMonthPicker && (
          <MonthPicker
            className="w-[356px]!"
            selectedDate={tempYear ?? value ?? new Date()}
            onSelect={handleMonthSelect}
            disabledMonth={isMonthDisabled}
            onYearClick={
              mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR
                ? () => {
                    setShowMonthPicker(false);
                    setShowYearPicker(true);
                  }
                : undefined
            }
            onPrev={
              mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR
                ? () => {
                    setTempYear((prev) =>
                      clampYearNavigation(new Date(prev ?? value ?? new Date()), -1)
                    );
                  }
                : undefined
            }
            onNext={
              mode === PICKER_MODE.MANDATORY_MONTH_SELECT_AND_OPTIONAL_YEAR
                ? () => {
                    setTempYear((prev) =>
                      clampYearNavigation(new Date(prev ?? value ?? new Date()), 1)
                    );
                  }
                : undefined
            }
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
